import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  useBoostPlantAdvertisingMutation,
  useBoostPlantMutation,
} from '@/api/queries/boost';
import { SendAnalitics } from '@/common/lib/playdeck';
import { playSound } from '@/common/lib/soundEffect';
import { type PlantType } from '@/common/types/common';
import { type BoostRequestType } from '@/common/types/query/boost';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringMain } from '@/components/Typography/Typography';
import { PurpleButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { BoostFirebaseEventsContext } from '@/context/BoostFirebaseEventContext';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface PaidBoostButtonProps {
  boostType: 'VIP' | 'Paid';
  plantType: PlantType;
  effectMultiplier: number;
  plantId?: string;
  tgemCost?: number;
}

const PaidBoostButton: FC<PaidBoostButtonProps> = ({
  plantType,
  boostType,
  plantId,
  tgemCost,
  effectMultiplier,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { openModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const [boostPlant] = useBoostPlantMutation({
    fixedCacheKey: 'shared-boost-mutation-request',
  });

  const [, adsBoostMutationState] = useBoostPlantAdvertisingMutation({
    fixedCacheKey: 'shared-ads-boost-mutation-request',
  });

  const { onBoostSuccessEvent, spendCoinEvent } = useContext(
    BoostFirebaseEventsContext,
  );

  const onPaidBoost = (): void => {
    playSound('buttons/boost');

    if ((tgemBalance || 0) < (tgemCost || 0)) {
      openModal({
        renderKind: 'coinDeposit',
        props: { source: 'paid_boost_no_balance' },
      });

      return;
    }
    setIsLoading(true);

    const hours = 2;

    const body: BoostRequestType = {
      plantType,
      boostType,
      hours,
      plantId: boostType === 'Paid' ? plantId : undefined,
    };

    const toastImageConfig = { img: tgemPng, webp: tgemWebp };

    boostPlant(body)
      .unwrap()
      .then(() => {
        const event =
          boostType === 'Paid' ? 'boost_paid_activated' : 'boost_vip_activated';
        const payload = {
          duration: hours * 60 * 60,
          multiplier: effectMultiplier,
        };
        onBoostSuccessEvent(event, payload);
        spendCoinEvent({
          currency_type: 't-gem',
          amount: tgemCost || 0,
          reason: 'paid_boost',
        });
        SendAnalitics.coinSpend({
          amount: tgemCost || 0,
          type: 'boost',
          item_name: `${effectMultiplier}x_boost_${plantType}`,
        });
        dispatch(addToBalance({ tgem: (tgemCost || 0) * -1 }));
        showToast(
          t(
            'You have spent {{value}} T-Gem to activate a speed boost with a x2 multiplier.',
            { value: tgemCost },
          ),
          toastImageConfig,
        );
      })
      .catch(() =>
        showToast(t("You don't have enough T-Gem"), toastImageConfig),
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="relative w-full">
      <PurpleButton
        isLoading={isLoading}
        disabled={isLoading || adsBoostMutationState.isLoading}
        onClick={onPaidBoost}
        className="relative !bg-black/20flex items-center gap-1 !h-[2rem] isolate"
      >
        <CurrencyImage kind="tgem" size="1.25rem" />
        <StringMain weight="extrabold" className={cn('huge-text')}>
          {tgemCost}
        </StringMain>
      </PurpleButton>
      {boostType === 'VIP' && (
        <div className="absolute -top-[4px] -right-[4px]">
          <Ticker variant="gold-small" text="-20%" />
        </div>
      )}
    </div>
  );
};

export { PaidBoostButton };
